<template>
  <div>
    <!-- Opened from AddItem through showStackedModal() (new Modal().show() + the shared
         #custom-overlay), like ModalSelectSubCategoryItem: z-index above the AddItem modal
         (1055) and the app sidenav (9999). -->
    <div
      class="modal fade"
      id="modalChoicePriceSchedules"
      tabindex="-1"
      aria-hidden="true"
      style="z-index: 10056"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Scheduled Price<span v-if="choiceName"> · {{ choiceName }}</span>
            </h5>
            <i
              class="material-icons btn-close-modal btn-close-modal-choice-price-schedules justify-content-end cursor-pointer"
              data-bs-dismiss="modal"
              aria-label="Close"
              >clear</i
            >
          </div>
          <div class="modal-body">
            <div class="ps-master">
              <div>
                <div class="ps-master__title">Scheduled Price</div>
                <div class="ps-master__sub">
                  When off, the choice always sells at its Default and Member Price and the
                  schedules below are kept.
                </div>
              </div>
              <div class="form-check form-switch mb-0 ms-auto">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="choice_price_schedules_enabled"
                  :checked="enabled"
                  @change="setEnabled($event.target.checked)"
                />
                <label class="form-check-label mb-0" for="choice_price_schedules_enabled">
                  {{ enabled ? "On" : "Off" }}
                </label>
              </div>
            </div>

            <div class="ps-hint">
              <i class="material-icons ps-hint__icon">info</i>
              <div>
                While on, inside a switched-on schedule (its dates, days and hours, Singapore time
                GMT+8), members and guests pay its price for this choice on MOO, QR and KIOSK. If
                schedules overlap, the one higher in the list applies. Outside every schedule the
                choice keeps its Default and Member Price. POS always keeps them.
              </div>
            </div>

            <div v-if="!rows.length" class="ps-empty">No schedules yet.</div>

            <div :class="{ 'ps-list--off': !enabled }">
            <div
              v-for="(schedule, index) in rows"
              :key="index"
              class="ps-card"
              :class="{ 'ps-card--off': !schedule.status, 'ps-card--invalid': errors[index] }"
            >
              <div class="ps-card__head">
                <span class="ps-card__title">Schedule {{ index + 1 }}</span>
                <span class="ps-card__move">
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === 0 }"
                    title="Move up (higher wins)"
                    @click="move(index, -1)"
                    >arrow_upward</i
                  >
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === rows.length - 1 }"
                    title="Move down"
                    @click="move(index, 1)"
                    >arrow_downward</i
                  >
                </span>
                <div class="form-check form-switch mb-0 ms-auto">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'choice_price_schedule_status_' + index"
                    :checked="!!schedule.status"
                    @change="schedule.status = $event.target.checked ? 1 : 0"
                  />
                  <label class="form-check-label mb-0" :for="'choice_price_schedule_status_' + index">
                    {{ schedule.status ? "On" : "Off" }}
                  </label>
                </div>
                <i
                  class="material-icons ps-card__remove"
                  title="Remove schedule"
                  @click="rows.splice(index, 1)"
                  >delete_outline</i
                >
              </div>

              <div class="ps-card__body">
                <ScheduleWhenFields
                  :schedule="schedule"
                  :id-prefix="'choice_price_schedule_' + index"
                  @update="Object.assign(schedule, $event)"
                />

                <div class="ps-field ps-price">
                  <div class="ps-field__label">Price ($)</div>
                  <!-- Text + decimal keypad rather than type="number", which some browsers let
                       letters into; setPrice() keeps digits and one dot, 2 decimals. -->
                  <input
                    type="text"
                    inputmode="decimal"
                    class="form-control ps-price__input"
                    :value="schedule.price"
                    @input="setPrice(schedule, $event)"
                    placeholder="0.00"
                  />
                </div>

                <div class="text-danger ps-error" v-if="errors[index]">{{ errors[index] }}</div>
              </div>
            </div>

            <button
              type="button"
              class="ps-add"
              :disabled="rows.length >= max"
              @click="addRow"
            >
              <i class="material-icons">add</i> Add schedule
            </button>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" data-bs-dismiss="modal">
              Cancel
            </button>
            <material-button class="btn btn-sm btn-background mb-0" @click="apply">
              Apply
            </material-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import MaterialButton from "@/components/MaterialButton.vue";
import { isUnrestrictedWhen, newScheduleWhen, scheduleWhenError, scheduleWhenFrom, scheduleWhenPayload } from "@/config/priceSchedule";
import ScheduleWhenFields from "@/components/ScheduleWhenFields.vue";

// Scheduled prices of one choice of a Choice item (switch + periods), built on
// ModalPromoCodeSchedules. Works on a copy: Apply checks it (same rules as the API) and hands it
// back to AddItem, which sends it with the item on Save; Cancel drops it.
export default {
  name: "ModalChoicePriceSchedules",
  components: { MaterialButton, ScheduleWhenFields },
  emits: ["apply"],
  data() {
    return {
      rows: [],
      // Master switch (minty_choices.price_schedules_enabled), saved together with the rows.
      enabled: false,
      choiceName: "",
      // Incomplete rows are flagged only after Apply; clashes show right away.
      checked: false,
      // Same limit as the API (MintyChoicePriceSchedule::MAX_PER_CHOICE).
      max: 10,
    };
  },
  computed: {
    // One message (or "") per row; a missing price only after Apply.
    errors() {
      return this.rows.map((schedule) => {
        const when = scheduleWhenError(schedule);
        if (when) {
          return when;
        }
        if (this.checked && !(parseFloat(schedule.price) > 0)) {
          return "Enter a price above 0.";
        }
        if (this.checked && isUnrestrictedWhen(schedule)) {
          return "Set dates, days or hours, or this price would apply all the time.";
        }
        return "";
      });
    },
  },
  methods: {
    // Fill the editor; AddItem shows the modal itself (showStackedModal).
    open(schedules, enabled, choiceName) {
      this.enabled = Number(enabled) === 1;
      this.rows = (schedules || []).map((schedule) => ({ ...schedule, ...scheduleWhenFrom(schedule) }));
      this.choiceName = choiceName || "";
      this.checked = false;
    },
    // Switching on with nothing listed starts a row to fill in.
    setEnabled(checked) {
      this.enabled = checked;
      if (checked && !this.rows.length) {
        this.addRow();
      }
    },
    addRow() {
      this.rows.push({ ...newScheduleWhen(), price: "", status: 1 });
    },
    // Overlapping schedules: the one higher in the list wins, so the order is the priority.
    move(index, step) {
      const target = index + step;
      if (target < 0 || target >= this.rows.length) {
        return;
      }
      const [row] = this.rows.splice(index, 1);
      this.rows.splice(target, 0, row);
    },
    // Digits and one dot only, at most 2 decimals; the field shows what is kept.
    setPrice(schedule, event) {
      const [whole, ...rest] = String(event.target.value).replace(/[^0-9.]/g, "").split(".");
      const price = rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
      schedule.price = price;
      event.target.value = price;
    },
    apply() {
      this.checked = true;
      if (this.errors.some(Boolean)) {
        return;
      }
      this.$emit("apply", {
        enabled: this.enabled ? 1 : 0,
        rows: this.rows.map((schedule) => ({
          ...scheduleWhenPayload(schedule),
          price: schedule.price,
          status: schedule.status ? 1 : 0,
        })),
      });
      $(".btn-close-modal-choice-price-schedules").click();
    },
  },
};
</script>

<style scoped>
.ps-master {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.ps-master__title {
  font-weight: 600;
  color: #344767;
}
.ps-master__sub {
  font-size: 13px;
  color: #7b809a;
}
.ps-list--off {
  opacity: 0.6;
}
.ps-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f4f6f9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #67748e;
}
.ps-hint__icon {
  font-size: 18px;
  color: #8a94a6;
}
.ps-empty {
  font-size: 14px;
  color: #7b809a;
  text-align: center;
  padding: 18px 0 8px;
}
.ps-error {
  font-size: 13px;
}
.ps-card {
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: visible;
}
.ps-card--off {
  background: #f8f9fa;
}
.ps-card--off .ps-card__body {
  opacity: 0.55;
}
.ps-card--invalid {
  border-color: #f44335;
}
.ps-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f3;
}
.ps-card__title {
  font-weight: 600;
  color: #344767;
}
.ps-card__move {
  display: inline-flex;
  gap: 2px;
  margin-left: 6px;
}
.ps-card__move .material-icons {
  font-size: 18px;
  color: #7b809a;
  cursor: pointer;
}
.ps-card__move .ps-card__move--off {
  opacity: 0.3;
  pointer-events: none;
}
.ps-card__remove {
  cursor: pointer;
  color: #9aa0ac;
  font-size: 20px;
}
.ps-card__remove:hover {
  color: #f44335;
}
.ps-card__body {
  padding: 12px 14px 14px;
  display: grid;
  gap: 14px;
}
.ps-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.ps-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ps-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}
.ps-datetime {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 8px;
}
.ps-date :deep(.dp__input) {
  font-size: 14px;
  color: #344767;
  height: 40px;
}
.ps-price {
  max-width: 200px;
}
/* The theme's .form-control:focus drops the border; keep it, like the date inputs beside it. */
.ps-price .ps-price__input,
.ps-price .ps-price__input:focus {
  height: 40px;
  font-size: 14px;
  color: #344767;
  padding: 6px 12px;
  border: 1px solid #ddd !important;
  border-radius: 4px;
  background-image: none;
  box-shadow: none;
}
/* Same hover / focus border as the date pickers (--dp-border-color-hover). */
.ps-price .ps-price__input:hover,
.ps-price .ps-price__input:focus {
  border-color: #aaaeb7 !important;
}
@media (max-width: 767px) {
  .ps-grid {
    grid-template-columns: 1fr;
  }
}
.ps-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #c4cad3;
  border-radius: 10px;
  background: transparent;
  color: #344767;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  justify-content: center;
  padding: 10px;
}
.ps-add:hover:not(:disabled) {
  background: #f4f6f9;
}
.ps-add:disabled {
  opacity: 0.5;
}
.ps-add .material-icons {
  font-size: 18px;
}
</style>
